import Backbone from 'backbone';

// Messages might not be unique, so use a unique Symbol as the key so that we remove the correct message when unregistering.
const beforeNavigateMessages: Map<Symbol, string> = new Map();

/**
 * Registers a navigation warning when the user tries to navigate to another Benchling page.
 * @param message Message to show to the user in a confirm dialog.
 * @returns Function to unregister the navigation warning.
 */
export function registerNavigationWarning(message: string): () => void {
  const key = Symbol(message);
  beforeNavigateMessages.set(key, message);
  return () => {
    beforeNavigateMessages.delete(key);
  };
}

/**
 * Clears all navigation warnings.
 */
export function clearNavigationWarnings(): void {
  beforeNavigateMessages.clear();
}

let previousUrl: string | null = null;

/**
 * Reset the stored previous URL. Should only be used by tests.
 */
export function _clearPreviousURL(): void {
  previousUrl = null;
}

/**
 * Returns the page's previous URL.
 * @returns A relative Benchling URL, an external URL, or an empty string.
 */
export function getPreviousURL(): string {
  return previousUrl ?? document.referrer;
}

export interface NavigateToURLOptions {
  skipTrigger?: boolean;
  replace?: boolean;
}

/**
 * Navigate to a given URL. Cancels navigation if there is a navigation warning that the user does not confirm.
 *
 * @param url the URL that we are navigating to
 * @param optionalParams If skipTrigger is true, then the route function for the new URL will not be invoked.
 *   if replace is true, the navigation will not create an entry in the browser's history
 *
 * @returns whether we have navigated to the new URL
 */
export default function navigateToURL(
  url: string,
  {skipTrigger = false, replace = false}: NavigateToURLOptions = {}
): boolean {
  // If the URL hasn't changed, don't do anything (to avoid a duplicate entry in the history)
  // Note: this will cause no routing to happen if just the window.location.hash is changing.
  const currentUrl = window.location.pathname + window.location.search;
  if (currentUrl === url) {
    return false;
  }

  if (beforeNavigateMessages.size > 0) {
    const confirmLeaveMessage = [...beforeNavigateMessages.values()].reverse().join('\n');
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmLeaveMessage)) {
      return false;
    }
  }

  previousUrl = currentUrl;

  Backbone.history.navigate(url, {trigger: !skipTrigger, replace});

  // See https://www.chromestatus.com/feature/6386758136627200
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0; // Safari: https://bugs.webkit.org/show_bug.cgi?id=5991

  return true;
}
